/**
 * @file useNegativeImpactCategories Hook
 * @description React Query hooks for negative impact category admin operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  negativeImpactCategoriesService,
  CreateNegativeImpactCategoryDto,
  UpdateNegativeImpactCategoryDto,
  CategoryWithUsageResponse,
} from '../services/negative-impact-categories.service';
import { unwrapResponse } from '../types';

import type { NegativeImpactCategory } from '@medsuite/models';

export const negativeImpactCategoryKeys = {
  all: ['negative-impact-categories'] as const,
  lists: () => [...negativeImpactCategoryKeys.all, 'list'] as const,
  list: () => [...negativeImpactCategoryKeys.lists(), 'all'] as const,
  active: () => [...negativeImpactCategoryKeys.lists(), 'active'] as const,
  details: () => [...negativeImpactCategoryKeys.all, 'detail'] as const,
  detail: (id: number) => [...negativeImpactCategoryKeys.details(), id] as const,
};

/**
 * Fetch all negative impact categories
 */
export function useNegativeImpactCategories(
  options?: Omit<UseQueryOptions<NegativeImpactCategory[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<NegativeImpactCategory[], Error>({
    queryKey: negativeImpactCategoryKeys.list(),
    queryFn: async () => {
      const response = await negativeImpactCategoriesService.getAll();
      return unwrapResponse<NegativeImpactCategory[]>(response.data);
    },
    ...options,
  });
}

/**
 * Fetch only active negative impact categories (for dropdowns)
 */
export function useActiveNegativeImpactCategories(
  options?: Omit<UseQueryOptions<NegativeImpactCategory[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<NegativeImpactCategory[], Error>({
    queryKey: negativeImpactCategoryKeys.active(),
    queryFn: async () => {
      const response = await negativeImpactCategoriesService.getActive();
      return unwrapResponse<NegativeImpactCategory[]>(response.data);
    },
    ...options,
  });
}

/**
 * Fetch a single category by ID with usage info
 */
export function useNegativeImpactCategory(
  id: number,
  options?: Omit<UseQueryOptions<CategoryWithUsageResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<CategoryWithUsageResponse, Error>({
    queryKey: negativeImpactCategoryKeys.detail(id),
    queryFn: async () => {
      const response = await negativeImpactCategoriesService.getById(id);
      return unwrapResponse<CategoryWithUsageResponse>(response.data);
    },
    ...options,
  });
}

/**
 * Create new negative impact category
 */
export function useCreateNegativeImpactCategory(
  options?: UseMutationOptions<NegativeImpactCategory, Error, CreateNegativeImpactCategoryDto>
) {
  const queryClient = useQueryClient();

  return useMutation<NegativeImpactCategory, Error, CreateNegativeImpactCategoryDto>({
    mutationFn: async (data) => {
      const response = await negativeImpactCategoriesService.create(data);
      return unwrapResponse<NegativeImpactCategory>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: negativeImpactCategoryKeys.lists() });
    },
    ...options,
  });
}

/**
 * Update negative impact category
 */
export function useUpdateNegativeImpactCategory(
  options?: UseMutationOptions<
    NegativeImpactCategory,
    Error,
    { id: number; data: UpdateNegativeImpactCategoryDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    NegativeImpactCategory,
    Error,
    { id: number; data: UpdateNegativeImpactCategoryDto }
  >({
    mutationFn: async ({ id, data }) => {
      const response = await negativeImpactCategoriesService.update(id, data);
      return unwrapResponse<NegativeImpactCategory>(response.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: negativeImpactCategoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: negativeImpactCategoryKeys.detail(variables.id) });
    },
    ...options,
  });
}

/**
 * Delete (deactivate) negative impact category
 */
export function useDeleteNegativeImpactCategory(
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await negativeImpactCategoriesService.delete(id);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: negativeImpactCategoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: negativeImpactCategoryKeys.detail(id) });
    },
    ...options,
  });
}
